import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, MapPinned, Phone, Navigation as Nav, Camera, X, Image as ImageIcon, Loader2, Share2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import RatingForm from "@/components/RatingForm";
import RatingDisplay from "@/components/RatingDisplay";
import DeliveryPhotoView from "@/components/DeliveryPhotoView";
import ProofPhotoView from "@/components/ProofPhotoView";
import OrderShareTrackingCard from "@/components/OrderShareTrackingCard";
import ShareTrackingModal from "@/components/ShareTrackingModal";
import OrderMatchMap from "@/components/OrderMatchMap";
import OrderTrackingMap from "@/components/OrderTrackingMap";
import GpsFreshnessBadge from "@/components/GpsFreshnessBadge";
import DriverOperationCard from "@/components/DriverOperationCard";
import MobileModal from "@/components/MobileModal";
import { formatCHF, formatKm, formatDateTime, SIZE_LABEL, DELIVERY_TYPE_LABEL } from "@/lib/format";
import { formatAddress } from "@/lib/formatAddress";
import { useDriverLocationBroadcaster } from "@/lib/useDriverLocationBroadcaster";
import { useWakeLock } from "@/lib/useWakeLock";

// Phase 43.14 — Set degli stati in cui il polling tracking del driver è attivo,
// allineato a quanto fa il customer (vedi pages/customer/OrderDetail.jsx).
const TRACKING_ACTIVE_STATUSES = new Set(["accepted", "picking_up", "picked_up", "delivering"]);

/**
 * Phase 31.3 — Client-side matching helper per la view dettaglio ordine driver.
 * Sceglie la prima route attiva del driver compatibile con pickup/delivery (haversine).
 */
function pickMatchedRoute(order, routes) {
  if (!order || !Array.isArray(routes) || routes.length === 0) return null;
  const { pickup_lat: pLat, pickup_lng: pLng, delivery_lat: dLat, delivery_lng: dLng } = order;
  if (![pLat, pLng, dLat, dLng].every(Number.isFinite)) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const hav = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const a =
      Math.sin(toRad(lat2 - lat1) / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lng2 - lng1) / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  };
  for (const r of routes) {
    if (r.active === false) continue;
    const { start_lat: sLat, start_lng: sLng, end_lat: eLat, end_lng: eLng } = r;
    if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) continue;
    const cap = typeof r.max_detour_km === "number" ? r.max_detour_km : 5;
    if (hav(pLat, pLng, sLat, sLng) <= cap && hav(dLat, dLng, eLat, eLng) <= cap) {
      return r;
    }
  }
  return null;
}

const NEXT_ACTION = {
  accepted: { next: "picking_up", label: "Vai al ritiro" },
  picking_up: { next: "picked_up", label: "Conferma ritiro" },
  picked_up: { next: "delivering", label: "Avvia consegna" },
  delivering: { next: "delivered", label: "Conferma consegna" },
};

const PHOTO_MODAL_COPY = {
  picked_up: {
    title: "Conferma ritiro",
    intro: "Firma accanto a 'Ritiro' e scatta una foto dove siano ben visibili: numero ordine, firma ritiro e pacco. La foto documenta il ritiro.",
    cta: "Conferma ritiro",
    success: "Ritiro confermato e foto inviata",
    notePlaceholder: "Nota ritiro (opz.): es. Ritirato da reception",
  },
  delivered: {
    title: "Conferma consegna",
    intro: "Fai firmare accanto a 'Consegna' (oppure firma tu se autorizzato) e scatta una foto dove siano ben visibili: numero ordine, firma consegna e pacco. La foto documenta la consegna.",
    cta: "Conferma consegna",
    success: "Consegna completata e foto inviata",
    notePlaceholder: "Nota consegna (opz.): es. Lasciato al portiere",
  },
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function DriverOrderDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 43.26.1 — header contestuale quando l'ordine arriva da Route Search
  const fromRouteSearch = searchParams.get("from") === "route_search";
  const rsDetour = parseFloat(searchParams.get("detour") || "0");
  const rsScore = parseInt(searchParams.get("score") || "0", 10);
  const rsEnd = searchParams.get("end") || "";
  // Phase 43.31 — preview mode da push notification (driver match fan-out)
  const fromPushPreview = searchParams.get("preview") === "1";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [matchedRoute, setMatchedRoute] = useState(null); // Phase 31.3
  // unified photo modal — kind = "picked_up" | "delivered"
  const [photoModal, setPhotoModal] = useState({ open: false, kind: null });
  const [photoFile, setPhotoFile] = useState(null); // {file, dataUrl}
  const [photoNote, setPhotoNote] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  // Phase 43.14 — live tracking allineato al customer
  const [tracking, setTracking] = useState(null);
  // Phase 43.35 — toggle opt-in per mostrare il "percorso fatto" (default: off)
  const [showCompletedRoute, setShowCompletedRoute] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      // Phase 31.3 — match contro le route del driver (client-side, nessun nuovo endpoint)
      try {
        const rr = await api.get("/driver/routes");
        const matched = pickMatchedRoute(data, Array.isArray(rr.data) ? rr.data : []);
        setMatchedRoute(matched);
      } catch {
        setMatchedRoute(null);
      }
      try {
        const r = await api.get(`/ratings/order/${id}`);
        setRatings(r.data);
      } catch {}
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Phase 44.22 — Flag aggressive-reload per gli stati di consegna critici.
  // Quando il driver è in picked_up/delivering, ogni "hidden" è quasi sempre
  // un'apertura intenzionale di Google Maps per raggiungere il destinatario.
  // Soglia visibilitychange in App.js scende da 8s a 1s → niente schermata
  // bianca al ritorno (anche per ritorni di pochi secondi). Cleanup automatico
  // su unmount o cambio stato.
  useEffect(() => {
    if (!order) return undefined;
    const critical = ["picked_up", "delivering"].includes(order.status);
    if (critical) {
      document.body.setAttribute("data-aggressive-reload", "true");
    } else {
      document.body.removeAttribute("data-aggressive-reload");
    }
    return () => {
      document.body.removeAttribute("data-aggressive-reload");
    };
  }, [order]);

  // Phase 43.14 — Live tracking polling (stessa logica del customer).
  // Aggiorna `tracking.driver.last_location` + ETA in tempo reale durante
  // accepted/picking_up/picked_up/delivering. Senza questo, la mappa driver
  // restava statica sulla snapshot iniziale.
  useEffect(() => {
    if (!order) return;
    let cancelled = false;
    const fetchTracking = async () => {
      try {
        const { data } = await api.get(`/orders/${id}/tracking`);
        if (!cancelled) setTracking(data);
      } catch { /* silent: l'UI resta sull'ultimo snapshot valido */ }
    };
    fetchTracking();
    if (TRACKING_ACTIVE_STATUSES.has(order.status)) {
      const t = setInterval(fetchTracking, 5_000);
      return () => { cancelled = true; clearInterval(t); };
    }
    return () => { cancelled = true; };
  }, [order?.status, id]);

  // Phase 43.14 — Auto-refresh ordine ogni 15s durante stati attivi per cogliere
  // cambi di status (es. customer annulla, admin sblocca) e tenere allineato il bottone
  // "Pacco ritirato" / "Consegna completata" senza richiedere refresh manuale.
  useEffect(() => {
    if (!order || !TRACKING_ACTIVE_STATUSES.has(order.status)) return;
    const t = setInterval(() => { load(); }, 15_000);
    return () => clearInterval(t);
  }, [order?.status, id]);

  // Phase 43.14 — Memo dei prop della mappa per evitare re-render inutili
  // (l'OrderTrackingMap ha questi nella dep list di un useEffect interno).
  // NB: broadcaster va istanziato PRIMA del mapDriver memo (ordine hooks).
  const broadcaster = useDriverLocationBroadcaster({ orderId: id, status: order?.status });

  const mapPickup = useMemo(() => (
    tracking?.pickup ? { lat: tracking.pickup.lat, lng: tracking.pickup.lng }
      : Number.isFinite(order?.pickup_lat) && Number.isFinite(order?.pickup_lng)
        ? { lat: order.pickup_lat, lng: order.pickup_lng } : null
  ), [tracking?.pickup, order?.pickup_lat, order?.pickup_lng]);

  const mapDelivery = useMemo(() => (
    tracking?.delivery ? { lat: tracking.delivery.lat, lng: tracking.delivery.lng }
      : Number.isFinite(order?.delivery_lat) && Number.isFinite(order?.delivery_lng)
        ? { lat: order.delivery_lat, lng: order.delivery_lng } : null
  ), [tracking?.delivery, order?.delivery_lat, order?.delivery_lng]);

  const mapDriver = useMemo(() => {
    // Phase 43.14 — prefer the freshest source:
    //   1) GPS lettura locale (lastCoords del broadcaster) — istantaneo
    //   2) tracking endpoint (echo dal backend) — fallback / per stati senza GPS attivo
    if (broadcaster?.lastCoords) {
      return {
        lat: broadcaster.lastCoords.lat,
        lng: broadcaster.lastCoords.lng,
        heading: broadcaster.lastCoords.heading,
      };
    }
    return tracking?.driver?.last_location
      ? {
          lat: tracking.driver.last_location.lat,
          lng: tracking.driver.last_location.lng,
          heading: tracking.driver.last_location.heading,
        }
      : null;
  }, [broadcaster?.lastCoords, tracking?.driver?.last_location]);

  const mapPhase = useMemo(() => (
    ["accepted", "picking_up"].includes(order?.status) ? "to_pickup"
      : ["picked_up", "delivering"].includes(order?.status) ? "to_delivery"
      : null
  ), [order?.status]);

  // Phase 42 — Wake Lock: tieni lo schermo acceso durante un tragitto attivo
  const wakeLockActive = !!order?.status && ["accepted", "picking_up", "picked_up", "delivering"].includes(order.status);
  const wake = useWakeLock(wakeLockActive);
  const [mapsConfirmOpen, setMapsConfirmOpen] = useState(false);

  const advance = async () => {
    const action = NEXT_ACTION[order.status];
    if (!action) return;
    // Apri modale foto se la prossima transizione richiede una foto firma (picked_up o delivered)
    if (action.next === "picked_up" || action.next === "delivered") {
      setPhotoFile(null);
      setPhotoNote("");
      setPhotoModal({ open: true, kind: action.next });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${id}/status`, { status: action.next });
      setOrder(data);
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Phase 43.9 — Preview mode: il driver può "Visualizza" un ordine pending
  // dalla lista disponibili e poi accettare dal dettaglio.
  const acceptFromPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${id}/accept`);
      setOrder(data);
      toast.success("Ordine accettato. Vai al ritiro.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onPickPhoto = () => fileRef.current?.click();

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Carica un'immagine (JPG, PNG, HEIC)");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Foto troppo grande (max 6 MB)");
      return;
    }
    const dataUrl = await fileToBase64(f);
    setPhotoFile({ file: f, dataUrl });
  };

  const closePhotoModal = () => {
    if (busy) return;
    setPhotoModal({ open: false, kind: null });
    setPhotoFile(null);
    setPhotoNote("");
  };

  const submitPhoto = async () => {
    const kind = photoModal.kind;
    if (!kind) return;
    if (!photoFile) {
      toast.error("Carica una foto prima di confermare");
      return;
    }
    const copy = PHOTO_MODAL_COPY[kind];
    setBusy(true);
    try {
      const body = { status: kind };
      if (kind === "picked_up") {
        body.pickup_photo_base64 = photoFile.dataUrl;
        body.pickup_note = photoNote.trim() || null;
      } else {
        body.delivery_photo_base64 = photoFile.dataUrl;
        body.delivery_note = photoNote.trim() || null;
      }
      const { data } = await api.patch(`/orders/${id}/status`, body);
      setOrder(data);
      setPhotoModal({ open: false, kind: null });
      setPhotoFile(null);
      setPhotoNote("");
      toast.success(copy.success);
      if (kind === "delivered") navigate("/driver/active");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Phase 43.8 — La funzione `cancel` lato driver è stata rimossa. Il driver
  // ora ha solo l'azione "Annulla e rilascia ordine" tramite ReleaseOrderModal,
  // disponibile per accepted/picking_up. Per stati post-ritiro deve contattare
  // il supporto (non può cancellare l'ordine del cliente unilateralmente).

  if (loading) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;
  if (!order) return <div className="card-soft p-10 text-center text-dapnow-muted">Ordine non trovato.</div>;

  const action = NEXT_ACTION[order.status];
  const isPreview = order.status === "pending" && (!order.driver_id || order.driver_id === user?.id);
  const targetAddr = order.status === "accepted" || order.status === "picking_up" ? order.pickup_address : order.delivery_address;
  const mapsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(targetAddr)}`;

  return (
    <div className="space-y-5" data-testid="driver-order-detail">
      {/* Phase 43.31 — Header contestuale: arrivato da push notification "new_matching_order" */}
      {fromPushPreview && !fromRouteSearch && (
        <div
          data-testid="push-preview-header"
          className="rounded-2xl border border-emerald-300 bg-gradient-to-r from-emerald-50 to-white px-4 py-3 flex items-center gap-3"
        >
          <div className="rounded-full bg-emerald-600 text-white p-2 shrink-0">
            <Nav className="size-4" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <p className="font-semibold text-emerald-900 leading-tight">
              Consegna compatibile con il tuo tragitto
            </p>
            <p className="text-xs text-emerald-900/80 mt-0.5">
              Hai ricevuto questa notifica perché un tuo tragitto attivo (abituale o "Sulla mia strada") corrisponde a questo ordine.
            </p>
          </div>
        </div>
      )}

      {/* Phase 43.26.1 — Header contestuale: "Trovata sulla tua strada" */}
      {fromRouteSearch && (
        <div
          data-testid="route-search-context-header"
          className="rounded-2xl border border-dapnow-blue/30 bg-gradient-to-r from-blue-50 to-white px-4 py-3 flex items-center gap-3"
        >
          <div className="rounded-full bg-dapnow-blue text-white p-2 shrink-0">
            <Nav className="size-4" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <p className="font-semibold text-dapnow-ink leading-tight">
              Trovata sulla tua strada{rsEnd ? ` verso ${rsEnd.split(",")[0]}` : ""}
            </p>
            <p className="text-xs text-dapnow-muted mt-0.5">
              {rsDetour > 0 && <>Deviazione stimata · <strong>{rsDetour.toFixed(2)} km</strong></>}
              {rsDetour > 0 && rsScore > 0 && " · "}
              {rsScore > 0 && <>Compatibilità · <strong>{rsScore}%</strong></>}
            </p>
          </div>
        </div>
      )}

      {/* Phase 43.46 — Sticky top alerts. AppShell header è h-20 (80px), quindi
          top deve essere 5rem (e non 4rem come era nel codice precedente, che
          creava 16px di overlap su iPhone con safe-area-inset-top). */}
      <div
        className="sticky z-20 -mx-1 px-1 pt-2 pb-1 space-y-2 bg-dapnow-bg/95 backdrop-blur supports-[backdrop-filter]:bg-dapnow-bg/75"
        data-testid="driver-sticky-alerts"
        style={{
          top: "calc(5rem + env(safe-area-inset-top, 0px))",
          paddingTop: "0.5rem",
        }}
      >
        {isPreview && (
          <div
            className="rounded-2xl bg-dapnow-ink text-white px-4 py-2.5 flex items-center justify-between gap-3 shadow-md"
            data-testid="driver-preview-banner"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <Nav className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 leading-tight">
                <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Anteprima ordine</p>
                <p className="text-sm font-bold truncate">Controlla i dettagli, poi accetta</p>
              </div>
            </div>
            <button
              onClick={acceptFromPreview}
              disabled={busy}
              data-testid="driver-preview-accept-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 disabled:opacity-60 transition flex-shrink-0"
            >
              {busy ? "Accetto…" : "Accetta ordine"}
            </button>
          </div>
        )}

        {action && (
          <DriverOperationCard order={order} busy={busy} onAdvance={advance} />
        )}

        {broadcaster.error && broadcaster.active && (
          <div
            className="rounded-2xl bg-amber-50 border border-amber-300 px-3 py-2 flex items-start gap-2 shadow-sm"
            data-testid="driver-sticky-gps-error"
          >
            <MapPinned className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-900">
                {broadcaster.errorCode === "permission_denied" ? "Posizione bloccata dal browser" : "Problema GPS"}
              </p>
              <p className="text-[11px] text-amber-800 leading-snug">{broadcaster.error}</p>
            </div>
            <button
              type="button"
              onClick={broadcaster.retry}
              data-testid="driver-sticky-gps-retry"
              className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold px-2.5 py-1 flex-shrink-0"
            >
              Riprova
            </button>
          </div>
        )}

        {/* Phase 44.16 — Banner "Posizione condivisa" sticky RIMOSSO.
            Era invasivo (copriva la card ordine) e ridondante: c'è già un
            indicator equivalente inline più sotto (testid `gps-broadcasting`).
            Mantenuto SOLO l'error banner sticky perché critico operativo. */}
      </div>

      <Link to="/driver/active" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Le mie consegne
      </Link>

      <div className="card-soft overflow-hidden">
        <div className="p-5 sm:p-7 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="overline">Consegna</p>
              <h1 className="font-display text-2xl font-bold tracking-tight">#{order.id.slice(0, 8).toUpperCase()}</h1>
              <p className="text-xs text-dapnow-muted">{formatDateTime(order.created_at)}</p>
            </div>
            <StatusBadge status={order.status} />
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl bg-dapnow-bg p-3">
              <p className="overline text-[10px]">Distanza</p>
              <p className="font-display text-lg font-bold tracking-tight">{formatKm(order.distance_km)}</p>
            </div>
            <div className="rounded-2xl bg-dapnow-bg p-3">
              <p className="overline text-[10px]">Tipo</p>
              <p className="font-display text-lg font-bold tracking-tight">{DELIVERY_TYPE_LABEL[order.delivery_type || order.urgency]}</p>
            </div>
            <div className="rounded-2xl bg-dapnow-blue text-white p-3">
              <p className="overline text-[10px] text-white/80">Valore contabile</p>
              <p className="font-display text-lg font-extrabold tracking-tight">{formatCHF(order.driver_earning)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card-soft p-5 sm:p-6 space-y-4">
        <h2 className="font-display text-lg font-bold tracking-tight">Indirizzi</h2>

        {/* Phase 43.14 — Mappa tracking allineata al customer: usa tracking endpoint
            (driver_last_location), polling 5s durante stati attivi, props memoizzati,
            phase corretta, 3 layer colori, badge GPS freshness. */}
        {mapPickup && mapDelivery && (
          <div className="rounded-2xl overflow-hidden border border-dapnow-line" data-testid="driver-order-route-map">
            <OrderTrackingMap
              pickup={mapPickup}
              delivery={mapDelivery}
              driver={mapDriver}
              phase={mapPhase}
              showCompleted={showCompletedRoute}
              className="h-52 sm:h-64 w-full"
            />
            {tracking?.tracking_active && (
              <div className="px-3 pt-3 sm:px-5 sm:pt-4 flex items-center justify-between gap-3 flex-wrap">
                <GpsFreshnessBadge
                  trackingActive={tracking.tracking_active}
                  lastLocationAtIso={tracking?.driver?.last_location_at}
                />
                <button
                  type="button"
                  onClick={() => setShowCompletedRoute((v) => !v)}
                  data-testid="toggle-completed-route"
                  className="text-[11px] font-semibold text-dapnow-muted hover:text-dapnow-ink underline-offset-2 hover:underline"
                >
                  {showCompletedRoute ? "Nascondi percorso fatto" : "Mostra percorso fatto"}
                </button>
              </div>
            )}
          </div>
        )}

        <Place title="Ritiro" addr={formatAddress(order.pickup_address)} name={order.pickup_contact_name} phone={order.pickup_contact_phone} dot="bg-dapnow-blue" />
        <Place title="Consegna" addr={formatAddress(order.delivery_address)} name={order.delivery_contact_name} phone={order.delivery_contact_phone} dot="bg-dapnow-red" />

        {/* Phase 31.3 — Compatibilità tragitto pendolare (UI-only, calcolato client-side) */}
        {matchedRoute && (
          <div className="border-t border-dapnow-line pt-4">
            <OrderMatchMap
              pickupLat={order.pickup_lat}
              pickupLng={order.pickup_lng}
              deliveryLat={order.delivery_lat}
              deliveryLng={order.delivery_lng}
              routeStartLat={matchedRoute.start_lat}
              routeStartLng={matchedRoute.start_lng}
              routeEndLat={matchedRoute.end_lat}
              routeEndLng={matchedRoute.end_lng}
              maxDetourKm={typeof matchedRoute.max_detour_km === "number" ? matchedRoute.max_detour_km : 5}
              matchedRouteName={matchedRoute.route_name}
              usualTimeWindow={matchedRoute.usual_time_window}
            />
          </div>
        )}

        <div className="border-t border-dapnow-line pt-4">
          <p className="overline text-[10px]">Pacco</p>
          <p className="text-sm font-medium mt-1">{order.package_description}</p>
          <p className="text-xs text-dapnow-muted">{SIZE_LABEL[order.package_size || order.size]}</p>
          {order.notes && <p className="text-xs text-dapnow-muted mt-1">Note: {order.notes}</p>}
        </div>

        {broadcaster.active && !broadcaster.error && broadcaster.lastPing && (
          <div className="border-t border-dapnow-line pt-4 flex items-center gap-2.5 rounded-xl bg-emerald-50 p-3" data-testid="gps-broadcasting">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-600"></span>
            </span>
            <p className="text-xs text-emerald-800 font-semibold flex-1 min-w-0">
              Ultima posizione inviata alle {broadcaster.lastPing.toLocaleTimeString("it-CH")}
            </p>
            {/* Phase 44.16 — CTA "Condividi tracking" inline (era nello sticky
                invasivo, ora compatto qui dentro la card). */}
            {["accepted", "picking_up", "picked_up", "delivering"].includes(order.status) && (
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                data-testid="driver-share-tracking-btn"
                className="text-[11px] font-bold text-emerald-700 hover:underline shrink-0"
              >
                Invia link
              </button>
            )}
          </div>
        )}
        {/* Phase 43.7 — Banner GPS error inline rimosso, ora è in sticky top. */}
        {!broadcaster.supported && order.status !== "delivered" && (
          <div className="border-t border-dapnow-line pt-4 flex items-center gap-2.5 rounded-xl bg-zinc-100 p-3">
            <MapPinned className="h-4 w-4 text-dapnow-muted" />
            <p className="text-xs text-dapnow-muted flex-1">GPS non disponibile su questo dispositivo</p>
          </div>
        )}
      </div>

      <div className="card-soft p-5 sm:p-6 space-y-3">
        {/* Phase 43.61 — Eliminato il riquadro CTA inferiore (Apri navigatore /
            Avvia consegna / Condividi tracking). La DriverOperationCard è ora
            l'unico centro azione. Resta solo "Annulla e rilascia ordine" o
            l'hint di no-cancel per coerenza compliance.  */}
        {/* Phase 43.8 — Azione unica di rilascio (no più "Annulla consegna" lato driver). */}
        {["accepted", "picking_up"].includes(order.status) && (
          <button
            type="button"
            onClick={() => setReleaseOpen(true)}
            disabled={busy}
            data-testid="driver-release-btn"
            className="btn-ghost w-full text-amber-700 border-amber-300/60 hover:bg-amber-50"
          >
            Annulla e rilascia ordine
          </button>
        )}
        {["picked_up", "delivering"].includes(order.status) && (
          <p className="text-xs text-dapnow-muted text-center pt-2" data-testid="driver-no-cancel-hint">
            Hai già ritirato il pacco. Se non puoi completare la consegna, contatta il supporto.
          </p>
        )}
      </div>

      {/* Phase 44.22 — Card "Invia il link al destinatario" prominente nella
          schermata ordine driver (richiesta utente). Read-only: il driver
          può aprire/copiare il link già generato dal cliente, non creare
          uno nuovo. Visibile durante stati tracking-attivi. */}
      {["accepted", "picking_up", "picked_up", "delivering"].includes(order.status) && (
        <OrderShareTrackingCard orderId={order.id} readOnly testid="driver-order-share-card" />
      )}

      {/* Phase 38 — Modale annulla ritiro */}
      {releaseOpen && (
        <ReleaseOrderModal
          orderId={order.id}
          busy={busy}
          onClose={() => setReleaseOpen(false)}
          onConfirmed={() => {
            setReleaseOpen(false);
            toast.success("Ordine rilasciato. Sarà visibile ad altri driver compatibili.");
            navigate("/driver");
          }}
        />
      )}

      {/* Phase 42 — Modale conferma "Apri Maps" con warning tracking */}
      {mapsConfirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden"
          data-testid="maps-confirm-modal"
          style={{ paddingTop: "max(0px, env(safe-area-inset-top))", paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}
        >
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 shadow-2xl overflow-y-auto" style={{ maxHeight: "85vh" }}>
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 flex-shrink-0">
                <Nav className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h3 className="font-display text-lg font-bold tracking-tight">Apri Google Maps?</h3>
                <p className="text-sm text-dapnow-muted mt-1">
                  Se apri Google Maps, il <strong>tracking live potrebbe interrompersi</strong>. Il cliente vedrà l'ultimo aggiornamento disponibile finché non torni nell'app DapNow.
                </p>
                <p className="text-xs text-dapnow-muted mt-2">
                  Suggerimento: usa la modalità split-screen del telefono per tenere DapNow visibile durante la navigazione.
                </p>
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={() => setMapsConfirmOpen(false)}
                className="btn-ghost"
                data-testid="maps-confirm-cancel"
              >
                Annulla
              </button>
              <a
                href={mapsHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMapsConfirmOpen(false)}
                className="btn-primary bg-amber-600 hover:bg-amber-700"
                data-testid="maps-confirm-open"
              >
                Apri comunque
              </a>
            </div>
          </div>
        </div>
      )}

      {order.status === "delivered" && order.delivery_photo_url && (
        <DeliveryPhotoView orderId={order.id} testid="driver-delivery-photo" />
      )}
      {["picked_up", "delivering", "delivered"].includes(order.status) && order.pickup_photo_url && (
        <ProofPhotoView
          orderId={order.id}
          kind="pickup"
          testid="driver-pickup-photo"
          defaultExpanded={order.status === "delivered"}
        />
      )}

      {order.status === "delivered" && (
        <div className="grid lg:grid-cols-2 gap-5">
          {!ratings.find((r) => r.from_role === "driver") ? (
            <RatingForm orderId={order.id} otherName={order.customer_name} role="driver" onDone={load} />
          ) : (
            <div className="card-soft p-5 sm:p-6" data-testid="rating-readonly">
              <p className="overline">La tua valutazione</p>
              {ratings.filter((r) => r.from_role === "driver").map((r) => (
                <div key={r.id} className="mt-2">
                  <RatingDisplay avg={r.score} count={1} size="lg" />
                  {r.comment && <p className="mt-2 text-sm text-dapnow-muted">"{r.comment}"</p>}
                </div>
              ))}
            </div>
          )}
          {ratings.filter((r) => r.from_role === "customer").map((r) => (
            <div key={r.id} className="card-soft p-5 sm:p-6" data-testid="rating-from-customer">
              <p className="overline">Il cliente ti ha valutato</p>
              <div className="mt-2"><RatingDisplay avg={r.score} count={1} size="lg" /></div>
              {r.comment && <p className="mt-2 text-sm text-dapnow-muted">"{r.comment}"</p>}
            </div>
          ))}
        </div>
      )}

      {/* Proof of Pickup / Delivery modal — unificato su MobileModal (Phase 44.16)
          Bug iPhone reale: backdrop trasparente, modal fuori viewport, body scrollabile.
          Root cause: era un fixed inset custom con items-end + 85vh + no body lock. */}
      {photoModal.open && (() => {
        const copy = PHOTO_MODAL_COPY[photoModal.kind] || PHOTO_MODAL_COPY.delivered;
        const tidPrefix = photoModal.kind === "picked_up" ? "pickup" : "pod";
        // Phase 44.17 — Footer sticky con CTA sempre visibili.
        const photoFooter = (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={closePhotoModal}
              disabled={busy}
              data-testid={`${tidPrefix}-cancel`}
              className="btn-ghost flex-1"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={submitPhoto}
              disabled={busy || !photoFile}
              data-testid={`${tidPrefix}-confirm`}
              className="btn-primary flex-1 justify-center"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Invio…</> : copy.cta}
            </button>
          </div>
        );
        return (
          <MobileModal
            open={photoModal.open}
            onClose={closePhotoModal}
            title={copy.title}
            testid={`${tidPrefix}-modal`}
            size="md"
            closeOnBackdrop={!busy}
            footer={photoFooter}
          >
            <div className="space-y-4">
              <p className="text-sm text-dapnow-muted flex items-start gap-2">
                <Camera className="h-4 w-4 text-dapnow-blue mt-0.5 shrink-0" />
                <span>{copy.intro}</span>
              </p>

              {photoModal.kind === "picked_up" && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs font-medium text-amber-900" data-testid="pickup-modal-checklist">
                  <p className="font-bold mb-1">Verifica nella foto:</p>
                  <ul className="space-y-0.5 list-disc list-inside">
                    <li>Numero ordine <span className="font-mono font-bold">#{order.id.slice(0, 8).toUpperCase()}</span> visibile</li>
                    <li>Firma accanto a "Ritiro" leggibile</li>
                    <li>Pacco intero inquadrato</li>
                  </ul>
                </div>
              )}
              {photoModal.kind === "delivered" && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs font-medium text-amber-900" data-testid="delivery-modal-checklist">
                  <p className="font-bold mb-1">Verifica nella foto:</p>
                  <ul className="space-y-0.5 list-disc list-inside">
                    <li>Numero ordine <span className="font-mono font-bold">#{order.id.slice(0, 8).toUpperCase()}</span> visibile</li>
                    <li>Firma accanto a "Consegna" leggibile</li>
                    <li>Pacco visibile (sulla porta o nelle mani del destinatario)</li>
                  </ul>
                </div>
              )}

              {photoFile ? (
                <div className="relative">
                  {/* Phase 44.21 — Photo preview ASPECT-RATIO PRESERVANTE.
                      Era: `h-40 object-cover` → foto verticali iPhone (3:4
                      portrait) venivano croppate in una striscia orizzontale
                      visibile solo al centro (l'utente vedeva 1/3 della foto).
                      Ora: `object-contain` + `max-h-[60vh]` su sfondo grigio
                      → foto verticali si vedono INTERE con barre laterali,
                      foto orizzontali si vedono INTERE, niente crop mai.
                      `max-h-[60vh]` impedisce che una foto verticale gigante
                      sfori il viewport (il body del modal scrolla comunque
                      grazie a flex-1 min-h-0 overflow-y-auto). */}
                  <img
                    src={photoFile.dataUrl}
                    alt={copy.title}
                    data-testid={`${tidPrefix}-photo-preview`}
                    className="w-full max-h-[60vh] object-contain rounded-2xl border border-dapnow-line bg-zinc-100"
                  />
                  <button
                    type="button"
                    onClick={() => setPhotoFile(null)}
                    data-testid={`${tidPrefix}-photo-clear`}
                    className="absolute top-2 right-2 h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white/90 backdrop-blur text-dapnow-red border border-dapnow-line hover:bg-white"
                    aria-label="Rimuovi foto"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onPickPhoto}
                  data-testid={`${tidPrefix}-photo-pick`}
                  className="w-full rounded-2xl border-2 border-dashed border-dapnow-line bg-dapnow-bg/50 p-6 flex flex-col items-center gap-2 hover:border-dapnow-blue hover:bg-dapnow-blue-soft transition"
                >
                  <ImageIcon className="h-8 w-8 text-dapnow-muted" />
                  <span className="font-semibold text-sm tracking-tight">Tocca per scattare o caricare</span>
                  <span className="text-xs text-dapnow-muted">Immagine, max 6 MB</span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPhotoChange}
                className="hidden"
                data-testid={`${tidPrefix}-photo-input`}
              />

              <textarea
                value={photoNote}
                onChange={(e) => setPhotoNote(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder={copy.notePlaceholder}
                data-testid={`${tidPrefix}-note`}
                className="w-full rounded-xl border border-dapnow-line p-3 text-sm focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none resize-none"
              />
            </div>
          </MobileModal>
        );
      })()}
      <ShareTrackingModal orderId={order.id} open={shareOpen} onClose={() => setShareOpen(false)} readOnly />
    </div>
  );
}

function Place({ title, addr, name, phone, dot }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${dot} ring-4 ring-dapnow-blue-soft shrink-0`} />
      <div className="flex-1">
        <p className="overline text-[10px]">{title}</p>
        <p className="font-medium flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-dapnow-muted" /> {addr}</p>
        {(name || phone) && (
          <p className="text-xs text-dapnow-muted mt-0.5 inline-flex items-center gap-1">
            {name} {phone ? <><span>·</span><Phone className="h-3 w-3" /> {phone}</> : null}
          </p>
        )}
      </div>
    </div>
  );
}


function ReleaseOrderModal({ orderId, busy: parentBusy, onClose, onConfirmed }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    const r = reason.trim();
    if (r.length < 3) {
      toast.error("Inserisci un motivo (almeno 3 caratteri).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/release`, { reason: r });
      onConfirmed?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="driver-release-modal">
      <div className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-xl font-bold tracking-tight">Annulla ritiro</h3>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-zinc-100" data-testid="driver-release-close" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-dapnow-muted mt-2">
          L'ordine tornerà visibile ad altri driver compatibili. Dicci il motivo (visibile solo a DapNow):
        </p>
        <select
          data-testid="driver-release-reason-preset"
          value=""
          onChange={(e) => e.target.value && setReason(e.target.value)}
          className="input-tall mt-3 w-full"
        >
          <option value="">Seleziona un motivo rapido…</option>
          <option value="Imprevisto personale">Imprevisto personale</option>
          <option value="Problema veicolo">Problema veicolo</option>
          <option value="Tragitto modificato">Tragitto modificato</option>
          <option value="Tempi non compatibili">Tempi non compatibili</option>
          <option value="Pacco non come descritto">Pacco non come descritto</option>
        </select>
        <textarea
          data-testid="driver-release-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Aggiungi dettagli (es. emergenza familiare, traffico imprevisto…)"
          className="input-tall mt-2 w-full resize-none"
          maxLength={300}
        />
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost" data-testid="driver-release-cancel-btn" disabled={submitting}>
            Annulla
          </button>
          <button
            onClick={submit}
            disabled={submitting || parentBusy}
            data-testid="driver-release-confirm-btn"
            className="btn-primary bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? "Rilascio…" : "Conferma annullamento"}
          </button>
        </div>
      </div>
    </div>
  );
}



